<template>
  <base-modal
    :is-open="isOpen"
    :modal-title="modalTitle"
    size="md"
    @onClose="handleClose"
    :titleSubmit="isEditMode ? 'Update' : 'Connect'"
    :useCancel="true"
    :forceLoading="loading"
    @onSubmit="handleSubmit"
  >
    <div class="container py-3 px-4">
      <div class="row g-3">
        <!-- Step 1: Connect -->
        <template v-if="!profile">
          <div class="col-12">
            <p class="text-muted">
              Click the button below to connect your Threads account via OAuth.
              You will be redirected to Threads to authorize the integration.
            </p>
          </div>
          <div class="col-12">
            <button
              class="btn btn-dark d-flex align-items-center gap-2"
              type="button"
              :disabled="oauthLoading"
              @click="openOAuthWindow"
            >
              <img
                src="/img/new-channels/threads.svg"
                alt="Threads"
                width="18"
                height="18"
                style="filter: invert(1)"
              />
              <span v-if="oauthLoading">Connecting...</span>
              <span v-else>Login with Threads</span>
            </button>
          </div>
        </template>

        <!-- Step 2: Connected - show profile and extra options -->
        <template v-else>
          <div class="col-12">
            <div class="d-flex align-items-center gap-3 p-3 bg-light rounded">
              <div class="avatar-sm">
                <div
                  class="avatar-title rounded-circle bg-dark text-white fs-18"
                >
                  <img
                    src="/img/new-channels/threads.svg"
                    alt="Threads"
                    width="20"
                    height="20"
                    style="filter: invert(1)"
                  />
                </div>
              </div>
              <div>
                <h6 class="mb-0">{{ profile.name }}</h6>
                <small class="text-muted">@{{ profile.username }}</small>
              </div>
              <button
                v-if="!isEditMode"
                class="btn btn-sm btn-outline-danger ms-auto"
                type="button"
                @click="clearProfile"
              >
                Disconnect
              </button>
            </div>
          </div>

          <div class="col-12">
            <label class="form-label">Bot Type</label>
            <v-select
              label="name"
              v-model="form.bot_type"
              :options="['normal', 'assistant']"
            />
          </div>

          <div class="col-12" v-if="form.bot_type">
            <label class="form-label">Bot</label>
            <v-select
              label="name"
              v-model="form.bot_id"
              :options="listBot"
              :reduce="(b) => b.id"
            />
          </div>

          <div class="col-12">
            <label class="form-label">Business Unit</label>
            <v-select
              label="label"
              :options="businessUnit"
              placeholder="Select business unit"
              v-model="business_unit"
              @option:selected="onSelectBusinessUnit"
            />
          </div>

          <div class="col-12">
            <label class="form-label">Groups</label>
            <v-select
              label="label"
              :options="groupManagement"
              placeholder="Select group"
              v-model="group"
              @option:selected="onSelectGroup"
              @search="onSearchGroup"
            />
          </div>
        </template>
      </div>
    </div>
  </base-modal>
</template>

<script>
import { debounce } from "lodash";
import { mapGetters } from "vuex";
import vSelect from "vue-select";

export default {
  components: { vSelect },

  props: {
    isOpen: { type: Boolean, required: true },
    modalTitle: { type: String, default: "Add Threads" },
    formEdit: { type: Object, default: null },
  },

  emits: ["onClose", "onAdd"],

  computed: {
    ...mapGetters({
      botList: "bot/botList",
      assistantList: "gen-ai/assistant",
      businessUnit: "ticket/getBranchOffice",
      groupManagement: "ticket/getGroupManagement",
    }),
    listBot() {
      return this.form.bot_type === "assistant"
        ? this.assistantList
        : this.botList;
    },
    threadsAppId() {
      return import.meta.env.VITE_THREADS_APP_ID;
    },
    isEditMode() {
      return !!this.formEdit;
    },
  },

  watch: {
    formEdit: {
      handler(val) {
        if (!val) return;
        this.form.integrationId = val.id;
        this.form.threadsUserId = val.integration_data?.threadsUserId ?? null;
        this.form.accessToken = val.integration_data?.accessToken ?? null;
        this.form.accountName = val.integration_data?.accountName ?? null;
        this.form.username = val.integration_data?.username ?? null;
        this.form.bot_type = val.bot_type ?? null;
        this.form.bot_id = val.bot_id ?? null;
        this.form.business_unit_id = val.business_unit_id ?? null;
        this.form.group_id = val.group_id ?? null;
        this.business_unit = val.business_unit?.branch_office ?? null;
        this.group = val.group?.name ?? null;
        // Show the profile card so the OAuth step is skipped
        this.profile = {
          name: val.integration_data?.accountName,
          username: val.integration_data?.username,
        };
      },
      immediate: true,
    },
  },

  data() {
    return {
      oauthLoading: false,
      loading: false,
      profile: null,
      accessToken: null,
      business_unit: null,
      group: null,
      form: {
        channelName: "threads",
        integrationId: null,
        threadsUserId: null,
        accessToken: null,
        accountName: null,
        username: null,
        bot_type: null,
        bot_id: null,
        business_unit_id: null,
        group_id: null,
        status: true,
      },
      oauthWindow: null,
      messageListener: null,
    };
  },

  methods: {
    openOAuthWindow() {
      const redirectUri = window.location.origin + "/oauth/threads/callback";
      const scope = [
        "threads_basic",
        "threads_content_publish",
        "threads_read_replies",
        "threads_manage_replies",
        "threads_manage_insights",
        "threads_manage_mentions",
      ].join(",");

      const url =
        `https://threads.net/oauth/authorize` +
        `?client_id=${this.threadsAppId}` +
        `&redirect_uri=${encodeURIComponent(redirectUri)}` +
        `&scope=${scope}` +
        `&response_type=code`;

      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      this.oauthLoading = true;
      this.oauthWindow = window.open(
        url,
        "threads_oauth",
        `width=${width},height=${height},left=${left},top=${top}`
      );

      this.messageListener = (event) => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.type === "THREADS_AUTH_CODE") {
          window.removeEventListener("message", this.messageListener);
          this.messageListener = null;
          this.handleOAuthCode(event.data.code);
        } else if (event.data?.type === "THREADS_AUTH_ERROR") {
          window.removeEventListener("message", this.messageListener);
          this.messageListener = null;
          this.oauthLoading = false;
          // console.error("[Threads OAuth] Auth denied by user:", event.data);
          this.errorToast(
            event.data.errorDescription || "Threads authorization was denied."
          );
        }
      };
      window.addEventListener("message", this.messageListener);
    },

    async handleOAuthCode(code) {
      // console.log("[Threads OAuth] Received auth code:", code);
      try {
        const appId = this.$store.getters.appId.hashed;
        const redirectUri = window.location.origin + "/oauth/threads/callback";

        // console.log("[Threads OAuth] Exchanging code for token...", {
        //   code,
        //   redirect_uri: redirectUri,
        //   appId,
        // });

        // Exchange code for short-lived token
        const exchangeRes = await this.$axios.post(
          `/api/threads/oauth/exchange`,
          {
            code,
            redirect_uri: redirectUri,
            appId,
          }
        );
        // console.log("[Threads OAuth] Exchange response:", exchangeRes.data);

        const shortToken = exchangeRes.data.data.access_token;
        const threadsUserId = exchangeRes.data.data.user_id;
        // console.log(
        //   "[Threads OAuth] Short-lived token:",
        //   shortToken,
        //   "| User ID:",
        //   threadsUserId
        // );

        // Upgrade to long-lived token
        // console.log("[Threads OAuth] Fetching long-lived token...");
        const longTokenRes = await this.$axios.get(
          `/api/integration/threads/get-longlived-token`,
          { params: { accessToken: shortToken } }
        );
        // console.log(
        //   "[Threads OAuth] Long-lived token response:",
        //   longTokenRes.data
        // );

        const longToken = longTokenRes.data.access_token ?? shortToken;

        // console.log("[Threads OAuth] Using access token:", longToken);

        // Get user profile
        // console.log("[Threads OAuth] Fetching user profile...");
        const profileRes = await this.$axios.get(
          `/api/integration/threads/get-user-profile`,
          { params: { accessToken: longToken } }
        );
        // console.log("[Threads OAuth] User profile:", profileRes.data);

        this.profile = profileRes.data;
        this.form.accessToken = longToken;
        this.form.threadsUserId = String(profileRes.data.id ?? threadsUserId);
        this.form.accountName =
          profileRes.data.name ?? profileRes.data.username;
        this.form.username = profileRes.data.username;

        // console.log("[Threads OAuth] Form ready to submit:", {
        //   threadsUserId: this.form.threadsUserId,
        //   accountName: this.form.accountName,
        //   username: this.form.username,
        // });
      } catch (error) {
        // console.error("[Threads OAuth] Error during token exchange:", error);
        this.errorToast(error);
      } finally {
        this.oauthLoading = false;
      }
    },

    onSelectBusinessUnit(val) {
      this.form.business_unit_id = val.id;
      this.business_unit = val.label;
    },

    onSelectGroup(val) {
      this.form.group_id = val.value;
      this.group = val.label;
    },

    onSearchGroup: debounce(function (val) {
      this.$store.dispatch("ticket/getGroupManagement", val);
    }, 500),

    async handleSubmit() {
      if (!this.profile) {
        this.errorToast("Please connect your Threads account first.");
        return;
      }
      this.loading = true;
      try {
        await this.$axios.post(
          `api/${this.$store.getters.appId.hashed}/integrate`,
          this.form
        );
        this.successToast(
          this.isEditMode
            ? "Threads integration updated successfully!"
            : "Threads account connected successfully!"
        );
        this.$emit("onAdd");
        this.handleClose();
      } catch (error) {
        this.errorToast(error);
      } finally {
        this.loading = false;
      }
    },

    clearProfile() {
      this.profile = null;
      this.accessToken = null;
      this.form.accessToken = null;
      this.form.threadsUserId = null;
      this.form.accountName = null;
      this.form.username = null;
    },

    handleClose() {
      if (this.messageListener) {
        window.removeEventListener("message", this.messageListener);
        this.messageListener = null;
      }
      if (this.oauthWindow && !this.oauthWindow.closed) {
        this.oauthWindow.close();
      }
      this.clearProfile();
      this.form.integrationId = null;
      this.form.bot_type = null;
      this.form.bot_id = null;
      this.form.business_unit_id = null;
      this.form.group_id = null;
      this.business_unit = null;
      this.group = null;
      this.oauthLoading = false;
      this.$emit("onClose");
    },
  },

  beforeUnmount() {
    if (this.messageListener) {
      window.removeEventListener("message", this.messageListener);
    }
  },
};
</script>
